import { FunctionComponent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { OrganisationContext } from '../../../context';
import { getChannelDomain, getCssVar, getWebAppUrl, isAdminLayout, layoutPath } from '../../utils';
import { IconLabel } from '../IconLabel';

import classes from './LayoutSwitcher.module.scss';

export const LayoutSwitcher: FunctionComponent = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { pathname, search } = useLocation();

  const { storyId, cardId, chatId, organisationTagId, eventId, profileId } = useParams();

  const { organisation } = useContext(OrganisationContext);

  const readerHome = useMemo(() => '/', []);

  const adminHome = useMemo(() => '/admin', []);

  const mirrorPage = useMemo(() => {
    if (pathname === readerHome) {
      return adminHome;
    }

    if (pathname === adminHome) {
      return readerHome;
    }

    if (isAdminLayout()) {
      return `${pathname.replace(adminHome, '')}${search}`;
    }

    return `${adminHome}${pathname}${search}`;
  }, [adminHome, pathname, readerHome, search]);

  const navigatePath = useMemo(() => {
    switch (pathname) {
      //SHARED PAGES
      case layoutPath('/'):
      case layoutPath('/profile'):
      case layoutPath('/profile/edit'):
      case layoutPath('/profile/items'):
      case layoutPath(`/profile/${profileId}`):
      case layoutPath(`/profile/${profileId}/items`):
      case layoutPath('/pushNotifications'):
      case layoutPath('/newsletters'):
      case layoutPath('/chats'):
      case layoutPath(`/chats?chatId=${chatId}`):
      case layoutPath('/leaderBoard'):
      case layoutPath('/search'):
      case layoutPath(`/details/${storyId}/${cardId}`):
      case layoutPath('/storyTab1'):
      case layoutPath(`/storyTab1/${storyId}/items`):
      case layoutPath('/storyTab2'):
      case layoutPath(`/storyTab2/${storyId}/items`):
      case layoutPath('/storyTab3'):
      case layoutPath(`/storyTab3/${storyId}/items`):
      case layoutPath('/storyTab4'):
      case layoutPath(`/storyTab4/${storyId}/items`):
      case layoutPath('/storyTab5'):
      case layoutPath(`/storyTab5/${storyId}/items`):
      case layoutPath(`/pinnedStoryTab/${storyId}/items`):
      case layoutPath(`/pinnedStoryTab2/${storyId}/items`):
      case layoutPath(`/pinnedStoryTab3/${storyId}/items`):
      case layoutPath(`/pinnedStoryTab4/${storyId}/items`):
      case layoutPath(`/pinnedStoryTab5/${storyId}/items`):
      case layoutPath(`/tags/${organisationTagId}`):
      case layoutPath('/events'):
      case layoutPath(`/eventDetails/${eventId}`):
        return mirrorPage;
      //READER PAGES
      case '/userDirectory':
        return adminHome;
      //ADMIN PAGES
      case '/admin/storyTab':
      case '/admin/pinnedStoryTab':
      case '/admin/pinnedStoryTab2':
      case '/admin/pinnedStoryTab3':
      case '/admin/pinnedStoryTab4':
      case '/admin/pinnedStoryTab5':
      case '/admin/notifications':
        return readerHome;
      default:
        return isAdminLayout() ? readerHome : adminHome;
    }
  }, [
    adminHome,
    cardId,
    chatId,
    eventId,
    mirrorPage,
    organisationTagId,
    pathname,
    profileId,
    readerHome,
    storyId,
  ]);

  const label = useMemo(
    () => t(`common.${isAdminLayout() ? 'reader-view' : 'admin-dashboard'}`),
    [t]
  );

  return (
    <>
      <IconLabel
        iconId={'switch'}
        label={label}
        color={getCssVar('--base-link-text-color')}
        hoverColor={getCssVar('--base-link-text-hover-color')}
        onClick={() => navigate(navigatePath)}
      />

      {organisation.role && (
        <a href={`${getWebAppUrl(getChannelDomain())}/organisation`} className={classes['link']}>
          <IconLabel
            iconId={'switch'}
            label={t('common.org-dashboard')}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
          />
        </a>
      )}
    </>
  );
};
